import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowRight, CalendarClock, ClipboardList, Dumbbell, Send, Sparkles } from 'lucide-react';
import { useRevealOnScroll } from '../../ui';

interface HeroProps {
  onScrollTo: (sectionId: string) => void;
}

const HERO_PREFILL_STORAGE_KEY = 'trackwithai-start-prefill';

const suggestionChips = [
  'Build a 3-day strength plan for a busy week',
  'Lose fat without losing strength',
  'Get back in shape after a layoff',
  'Prep for a half-marathon in 12 weeks',
];

export function Hero({ onScrollTo }: HeroProps) {
  const revealRef = useRevealOnScroll<HTMLDivElement>();
  const navigate = useNavigate();
  const [heroInput, setHeroInput] = useState('');
  const [isHandingOff, setIsHandingOff] = useState(false);

  function launch(message: string) {
    const trimmed = message.trim();
    if (!trimmed || isHandingOff) {
      return;
    }
    try {
      window.sessionStorage.setItem(HERO_PREFILL_STORAGE_KEY, trimmed);
    } catch {
      // sessionStorage unavailable — navigate anyway
    }
    setIsHandingOff(true);
    window.setTimeout(() => {
      navigate('/start');
    }, 260);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    launch(heroInput);
  }

  return (
    <section
      className={`hero-handoff relative overflow-hidden pb-20 pt-12 lg:pb-28 lg:pt-16 ${
        isHandingOff ? 'hero-handoff--leaving' : ''
      }`}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(var(--color-brand-rgb),0.18),transparent_34%),radial-gradient(circle_at_82%_14%,rgba(var(--color-accent-rgb),0.16),transparent_32%),linear-gradient(180deg,rgba(255,252,245,0.55),transparent_70%)]"
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(420px,1.05fr)] lg:px-8">
        <div>
          <div className="app-chip app-chip--accent">
            <Sparkles className="h-4 w-4" />
            A real coach, in your pocket
          </div>

          <h1 className="display-serif mt-6 max-w-2xl text-5xl leading-[1.02] tracking-[-0.02em] text-[var(--color-ink)] sm:text-6xl lg:text-[5.2rem]">
            Your plan starts with a conversation.
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-8 text-[var(--color-muted)]">
            Skip the generic templates. Tell the coach what you&apos;re working toward and the week you actually live — in a few minutes you&apos;ll have a first plan built around your schedule, equipment, and goals.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/start" className="app-button app-button--primary px-5 py-3">
              Try The Coach
              <ArrowRight className="h-4 w-4" />
            </Link>
            <button
              type="button"
              onClick={() => onScrollTo('features')}
              className="app-button app-button--secondary px-5 py-3"
            >
              See How It Works
              <ArrowDown className="h-4 w-4" />
            </button>
          </div>

          <p className="mt-3 text-sm text-[var(--color-muted)]">
            Start with a 7-day free trial.
          </p>

          <div ref={revealRef} className="reveal-on-scroll reveal-stagger mt-10 grid gap-4 sm:grid-cols-3">
            <div className="app-mini-panel p-5">
              <CalendarClock className="h-5 w-5 text-[var(--color-brand)]" />
              <p className="mt-3 text-sm font-semibold text-[var(--color-ink)]">Built around your week</p>
              <p className="mt-2 text-sm leading-6 text-[var(--color-muted)]">Real schedule, real equipment, realistic session lengths.</p>
            </div>
            <div className="app-mini-panel p-5">
              <ClipboardList className="h-5 w-5 text-[var(--color-accent)]" />
              <p className="mt-3 text-sm font-semibold text-[var(--color-ink)]">See week one first</p>
              <p className="mt-2 text-sm leading-6 text-[var(--color-muted)]">A draft plan in a few minutes, before you commit.</p>
            </div>
            <div className="app-mini-panel p-5">
              <Dumbbell className="h-5 w-5 text-[var(--color-brand)]" />
              <p className="mt-3 text-sm font-semibold text-[var(--color-ink)]">Gets sharper each week</p>
              <p className="mt-2 text-sm leading-6 text-[var(--color-muted)]">Logs, check-ins, and real feedback tighten the plan.</p>
            </div>
          </div>
        </div>

        <div className="landing-chat-shell">
          <div className="landing-chat-shell__header">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[var(--color-muted)]">
                Start The Conversation
              </p>
              <h2 className="mt-2 text-xl font-semibold text-[var(--color-ink)]">
                What are you trying to work on?
              </h2>
            </div>
            <span className="app-chip app-chip--brand">
              Live
            </span>
          </div>

          <div className="landing-chat-shell__transcript">
            <div className="landing-message landing-message--assistant">
              <p>
                Hey — I&apos;m your coach. Tell me what you&apos;re working toward and a bit about your week, and I&apos;ll draft a first plan you can actually run.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="landing-hero-composer">
              <label htmlFor="hero-prefill" className="sr-only">
                What are your fitness goals?
              </label>
              <div className="landing-hero-composer__field">
                <textarea
                  id="hero-prefill"
                  value={heroInput}
                  onChange={(event) => setHeroInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      launch(heroInput);
                    }
                  }}
                  placeholder="What are your fitness goals?"
                  rows={2}
                  disabled={isHandingOff}
                  className="landing-hero-composer__input"
                />
                <button
                  type="submit"
                  disabled={!heroInput.trim() || isHandingOff}
                  className="landing-hero-composer__send"
                  aria-label="Send to coach"
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
              <div className="landing-hero-composer__chips">
                {suggestionChips.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => launch(chip)}
                    disabled={isHandingOff}
                    className="landing-hero-composer__chip"
                  >
                    {chip}
                  </button>
                ))}
              </div>
              <p className="landing-hero-composer__hint">
                Press Enter to send. We&apos;ll hand off to the coach and keep your message.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
